import { z } from "zod";

export type AssetType =
  "all" | "Toimisto" | "Liiketila" | "Logistiikka" | "Kampus" | "Asuin";
export type Status = "all" | "ok" | "warn" | "alert";

// Place is an opaque, operator-entered string — not a closed enum. "all" is the sentinel
// for "no place filter", consistent with `type`/`status` below. Valid options are derived
// from the currently loaded building set (see `liveCityCounts` in inventory-filter.tsx),
// not validated against a fixed list here.
export type InventoryFilter = {
  city: string;
  type: AssetType;
  status: Status;
};

export const DEFAULT_INVENTORY_FILTER: InventoryFilter = {
  city: "all",
  type: "all",
  status: "all",
};

const ASSET_TYPES: ReadonlyArray<Exclude<AssetType, "all">> = [
  "Toimisto",
  "Liiketila",
  "Logistiikka",
  "Kampus",
  "Asuin",
];
const STATUSES: ReadonlyArray<Exclude<Status, "all">> = ["ok", "warn", "alert"];

function fromAllowedList<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
): T | "all" {
  return value && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : "all";
}

/** Sibling of `fromAllowedList` for a facet with no closed vocabulary (place): any non-empty
 *  value passes through unchanged instead of being checked against a fixed list. */
function fromOpaqueString(value: string | undefined): string {
  return value && value.length > 0 ? value : "all";
}

// Sticky scope, carried in the URL search params (locked shape — see issue #536).
// English keys, existing API/domain strings as values.
export const inventorySearchSchema = z.object({
  building: z.string().optional(),
  group: z.string().optional(),
  city: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
});
export type InventorySearch = z.infer<typeof inventorySearchSchema>;

export type InventoryScope = {
  filter: InventoryFilter;
  groupId: string | null;
  buildingId: string | null;
};

/** Write rule: buildingId set → URL is only `?building=`; else group + non-"all" filters. */
export function encodeInventoryScope(scope: InventoryScope): InventorySearch {
  if (scope.buildingId) return { building: scope.buildingId };
  const search: InventorySearch = {};
  if (scope.groupId) search.group = scope.groupId;
  if (scope.filter.city !== "all") search.city = scope.filter.city;
  if (scope.filter.type !== "all") search.type = scope.filter.type;
  if (scope.filter.status !== "all") search.status = scope.filter.status;
  return search;
}

/** Read rule: `building` wins; else apply `group` + filters. */
export function decodeInventoryScope(search: InventorySearch): InventoryScope {
  if (search.building) {
    return {
      filter: DEFAULT_INVENTORY_FILTER,
      groupId: null,
      buildingId: search.building,
    };
  }
  return {
    filter: {
      city: fromOpaqueString(search.city),
      type: fromAllowedList(search.type, ASSET_TYPES),
      status: fromAllowedList(search.status, STATUSES),
    },
    groupId: search.group ?? null,
    buildingId: null,
  };
}

export function searchEqual(a: InventorySearch, b: InventorySearch): boolean {
  return (
    a.building === b.building &&
    a.group === b.group &&
    a.city === b.city &&
    a.type === b.type &&
    a.status === b.status
  );
}

/** Stable primitive key for an `InventorySearch` — the `useEffect` dependency every scoped
 *  fetch (ActivityFeed, RecommendationFeed, AgentTeamPanel) needs instead of the fresh object
 *  `encodeInventoryScope` returns each render. */
export function inventorySearchKey(search: InventorySearch): string {
  return `${search.building ?? ""}|${search.group ?? ""}|${search.city ?? ""}|${search.type ?? ""}|${search.status ?? ""}`;
}
